/**
 * Shared visual helpers for feed rows: event-type iconography, severity tones,
 * and relative-time formatting. Kept separate so both the row and the
 * expandable detail render effects consistently.
 */
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  CloudOff,
  Coins,
  Eye,
  Inbox,
  Info,
  PackagePlus,
  RotateCcw,
  Ship,
  Sparkles,
  Tag,
  TrendingUp,
  Unlink,
  type LucideIcon,
} from "lucide-react";

import type {
  CommercialDigestDimension,
  FeedEventType,
  FeedSeverity,
  ScopeDimension,
  TimeHorizon,
} from "@/types/feed";

export const EVENT_ICON: Record<FeedEventType, LucideIcon> = {
  batch_ingested: Inbox,
  new_booking: PackagePlus,
  rate_cards_ingested: Tag,
  data_source_stale: CloudOff,
  search_cycle_completed: Sparkles,
  recommendations_produced: TrendingUp,
  allocation_impact: AlertTriangle,
  booking_state_changed: Bell,
  vessel_delayed: Ship,
  booking_rolled: RotateCcw,
  cutoff_approaching: Clock,
  tracking_dropped: Unlink,
  operational_digest: CalendarClock,
  custom_event: Bell,
  commercial_digest: Coins,
  rebook_opportunity: TrendingUp,
  first_book: Tag,
  rebook_confirm: CheckCircle2,
  rebook_progress: Activity,
  alert_fired: AlertTriangle,
  insight_snapshot: Sparkles,
  pattern_detected: Activity,
  booking_watch_signal: Eye,
  marine_incident: AlertOctagon,
};

/**
 * Event types withheld from the Activity Feed for now (still stored server-side).
 * Keep in sync with `HIDDEN_FEED_EVENT_TYPES` in `tideway-core` domain_event.rs.
 */
export const HIDDEN_FEED_EVENT_TYPES = new Set<string>([
  "data_source_stale",
  "tracking_dropped",
  "search_cycle_completed",
  "recommendations_produced",
  "allocation_impact",
]);

export function isHiddenFeedEventType(eventType: string): boolean {
  return HIDDEN_FEED_EVENT_TYPES.has(eventType);
}

/** Human labels for feed event types (widget filter + tooltips). */
export const EVENT_TYPE_LABEL: Record<FeedEventType, string> = {
  batch_ingested: "Batch ingested",
  new_booking: "New booking",
  rate_cards_ingested: "Rate update",
  data_source_stale: "Stale data source",
  search_cycle_completed: "Search cycle",
  recommendations_produced: "Recommendations",
  allocation_impact: "Allocation impact",
  booking_state_changed: "Booking update",
  vessel_delayed: "Delayed",
  booking_rolled: "Booking rolled",
  cutoff_approaching: "Cutoff approaching",
  tracking_dropped: "Tracking lost",
  operational_digest: "Operational",
  custom_event: "Custom event",
  commercial_digest: "Commercial digest",
  rebook_opportunity: "Rebook",
  first_book: "First book",
  rebook_confirm: "Rebook detected",
  rebook_progress: "Rebook progress",
  alert_fired: "Alert",
  insight_snapshot: "Insight",
  pattern_detected: "Pattern",
  booking_watch_signal: "Watch",
  marine_incident: "Marine incident",
};

export const SEVERITY_LABEL: Record<FeedSeverity, string> = {
  info: "Info",
  warning: "Warning",
  urgent: "Urgent",
};

/** Value tone for severity icons on feed rows. */
export const SEVERITY_TEXT: Record<FeedSeverity, string> = {
  info: "text-violet-600 dark:text-violet-300",
  warning: "text-amber-600 dark:text-amber-300",
  urgent: "text-rose-600 dark:text-rose-300",
};

/** Compact severity glyph shown on feed row meta (replaces the Severity label). */
export const SEVERITY_ICON: Record<FeedSeverity, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  urgent: AlertOctagon,
};

/** Type-tinted card treatments — commercial digest rebook / first-book rows. */
export type FeedCardAccent = "rebook" | "first_book";

export const FEED_CARD_ACCENT_CLASS: Record<FeedCardAccent, string> = {
  rebook: "feed-event-row--accent-rebook",
  first_book: "feed-event-row--accent-first-book",
};

/** Left accent on feed cards so severity is scannable without an icon column. */
export const FEED_SEVERITY_BORDER: Record<FeedSeverity, string> = {
  info: "border-l-[3px] border-l-violet-500/45",
  warning: "border-l-[3px] border-l-amber-500/55",
  urgent: "border-l-[3px] border-l-rose-500/60",
};

/**
 * Root feed cards users can filter on (FeedFilterBar + home widget). Omits
 * hidden types (see `HIDDEN_FEED_EVENT_TYPES`), legacy combined digests
 * (`commercial_digest`), and per-booking cutoffs superseded by `operational_digest`.
 */
export const FEED_FILTER_EVENT_TYPES: FeedEventType[] = [
  "insight_snapshot",
  "pattern_detected",
  "rebook_opportunity",
  "first_book",
  "operational_digest",
  "booking_state_changed",
  "vessel_delayed",
  "booking_rolled",
  "alert_fired",
  "marine_incident",
  "rate_cards_ingested",
  "batch_ingested",
  "new_booking",
];

/** Feed type filters available for Client (consignee portal) users. */
export const CLIENT_FEED_FILTER_EVENT_TYPES: FeedEventType[] = [
  "operational_digest",
  "booking_state_changed",
  "vessel_delayed",
  "booking_rolled",
  "marine_incident",
];

export function feedFilterEventTypesForRole(
  role?: string | null,
): FeedEventType[] {
  return role === "Client"
    ? CLIENT_FEED_FILTER_EVENT_TYPES
    : FEED_FILTER_EVENT_TYPES;
}

/** @alias FEED_FILTER_EVENT_TYPES */
export const FEED_WIDGET_EVENT_TYPES = FEED_FILTER_EVENT_TYPES;

/** Tailwind classes for the icon chip, keyed by severity. */
export const SEVERITY_CHIP: Record<FeedSeverity, string> = {
  info: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  warning: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  urgent: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
};

export function eventIcon(type: FeedEventType): LucideIcon {
  return EVENT_ICON[type] ?? Bell;
}

/**
 * Whether a feed row matches an event-type filter. Split rebook / first-book
 * types also match leftover `commercial_digest` cards of the same dimension.
 */
export function feedEventMatchesType(
  eventType: string,
  wanted: string,
  payload?: unknown,
): boolean {
  if (eventType === wanted) {
    if (
      wanted === "batch_ingested" &&
      payload &&
      typeof payload === "object" &&
      (payload as { kind?: string }).kind === "new_booking_mail"
    ) {
      return false;
    }
    return true;
  }
  if (
    wanted === "new_booking" &&
    eventType === "batch_ingested" &&
    payload &&
    typeof payload === "object" &&
    (payload as { kind?: string }).kind === "new_booking_mail"
  ) {
    return true;
  }
  if (
    eventType === "commercial_digest" &&
    (wanted === "rebook_opportunity" || wanted === "first_book")
  ) {
    return commercialDigestDimension(payload) === wanted;
  }
  // Rebook confirmations and internal workflow-progress milestones share the
  // "Rebook" feed slice with opportunities, so the rebook_opportunity filter
  // also surfaces rebook_confirm and rebook_progress cards.
  if (
    wanted === "rebook_opportunity" &&
    (eventType === "rebook_confirm" || eventType === "rebook_progress")
  ) {
    return true;
  }
  return false;
}

/** Commercial-digest dimension when the payload is a digest root. */
export function commercialDigestDimension(
  payload: unknown,
): CommercialDigestDimension | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  if (record.kind !== "commercial_digest") return null;
  if (record.dimension === "rebook_opportunity") return "rebook_opportunity";
  if (record.dimension === "first_book") return "first_book";
  return null;
}

/** Persistent card glow for distinguished feed types (rebook / first book). */
export function feedEventCardAccent(
  payload?: unknown,
  eventType?: string,
): FeedCardAccent | null {
  if (eventType === "rebook_confirm" || isRebookConfirmPayload(payload)) {
    return "rebook";
  }
  if (eventType === "rebook_progress" || isRebookProgressPayload(payload)) {
    return "rebook";
  }
  const dimension = commercialDigestDimension(payload);
  if (dimension === "rebook_opportunity") return "rebook";
  if (dimension === "first_book") return "first_book";
  // Per-booking opportunity roots carry the split event type directly, so tint
  // them the same as the digest cards they replace.
  if (eventType === "rebook_opportunity") return "rebook";
  if (eventType === "first_book") return "first_book";
  return null;
}

export function feedEventCardAccentClass(
  payload?: unknown,
  eventType?: string,
): string | undefined {
  const accent = feedEventCardAccent(payload, eventType);
  return accent ? FEED_CARD_ACCENT_CLASS[accent] : undefined;
}

/** Whether a payload is a rebook-confirm card root. */
export function isRebookConfirmPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  return (payload as Record<string, unknown>).kind === "rebook_confirm";
}

/** Whether a payload is a rebook-progress milestone root. */
export function isRebookProgressPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  return (payload as Record<string, unknown>).kind === "rebook_progress";
}

/** Friendly label for a watch-tier signal keyed by its `payload.kind`. */
export function watchSignalKindLabel(payload?: unknown): string {
  const kind =
    payload && typeof payload === "object"
      ? (payload as { kind?: string }).kind
      : undefined;
  switch (kind) {
    case "transshipment_discharged":
      return "Transshipment";
    case "transshipment_loaded":
      return "Transshipment";
    case "documents_arrived":
      return "Documents";
    case "documents_missing":
      return "Documents";
    default:
      return "Watch";
  }
}

/** Primary type label for a feed row — mail / digest dimensions override generics. */
export function feedEventPrimaryTypeLabel(
  eventType: FeedEventType,
  graphMailCategory?: string | null,
  payload?: unknown,
): string {
  if (eventType === "rebook_opportunity") return "Rebook";
  if (eventType === "first_book") return "First book";
  if (eventType === "rebook_confirm") return "Rebook detected";
  if (eventType === "rebook_progress") return "Rebook";
  const dimension = commercialDigestDimension(payload);
  if (dimension === "rebook_opportunity") return "Rebook";
  if (dimension === "first_book") return "First book";
  if (graphMailCategory) return graphMailCategoryLabel(graphMailCategory);
  if (eventType === "booking_watch_signal") {
    return watchSignalKindLabel(payload);
  }
  if (eventType === "new_booking") return "New booking";
  if (
    payload &&
    typeof payload === "object" &&
    (payload as { kind?: string }).kind === "new_booking_mail"
  ) {
    return "New booking";
  }
  if (eventType === "custom_event") {
    const taskLabel = (payload as { task_label?: unknown } | null | undefined)
      ?.task_label;
    if (typeof taskLabel === "string" && taskLabel.trim()) return taskLabel.trim();
    return "Custom event";
  }
  return EVENT_TYPE_LABEL[eventType] ?? eventType;
}

/** Short label + tone for the planning-horizon badge on a feed row. */
export const HORIZON_BADGE: Record<TimeHorizon, { label: string; className: string }> = {
  immediate: {
    label: "Now",
    className: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  },
  developing: {
    label: "Developing",
    className: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  },
  strategic: {
    label: "Strategic",
    className: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  },
};

/** User-facing labels for the virtual scope dimensions. */
export const SCOPE_DIMENSION_LABEL: Record<ScopeDimension, string> = {
  my: "My Feed",
  booking: "Booking",
  customer: "Customer",
  sales_person: "Sales Person",
  operator: "Operator",
  agent: "Agent",
  carrier: "Carrier",
  lane: "Lane",
  management: "Management",
};

/** Dimensions offered to every role; role-specific ones are hidden below. */
export const SCOPE_DIMENSION_ORDER: ScopeDimension[] = [
  "my",
  "booking",
  "customer",
  "sales_person",
  "operator",
  "agent",
  "carrier",
  "lane",
  "management",
];

/**
 * Feed pills a role may pick. User (ops) already has the operator portfolio as
 * My Feed while "Show only my bookings" is on; SalesPerson already has the
 * sales-person portfolio as My Feed. Users assigned to an agent have that agent
 * portfolio as their My Feed, so the Agent pill is hidden for them (mirrors the
 * operator pattern).
 */
export function visibleScopeDimensions(
  role?: string | null,
  hasAgentAssignment?: boolean,
  scopeToMyBookings: boolean = true,
): ScopeDimension[] {
  return SCOPE_DIMENSION_ORDER.filter((dimension) => {
    if (dimension === "operator" && role === "User" && scopeToMyBookings) {
      return false;
    }
    if (dimension === "sales_person" && role === "SalesPerson") return false;
    if (dimension === "agent" && hasAgentAssignment) return false;
    return true;
  });
}

export function isScopeVisibleForRole(
  dimension: ScopeDimension,
  role?: string | null,
  hasAgentAssignment?: boolean,
  scopeToMyBookings: boolean = true,
): boolean {
  return visibleScopeDimensions(
    role,
    hasAgentAssignment,
    scopeToMyBookings,
  ).includes(dimension);
}

/** Human labels for the `scope_tags` chips shown on My Feed rows. */
export const SCOPE_TAG_LABEL: Record<string, string> = {
  booking: "Booking",
  customer: "Customer",
  sales_person: "Sales",
  opened_by: "Operator",
  agent: "Agent",
  carrier: "Carrier",
  lane: "Lane",
};

/**
 * Compact chip labels for classified Graph mail categories, mirroring the Rust
 * `graph_mail_category_label` groupings in `correlator.rs` but shortened for the
 * inline row chip. Falls back to a title-cased slug for unmapped categories.
 */
const GRAPH_MAIL_CHIP_LABEL: Record<string, string> = {
  new_booking: "New booking",
  order_confirmation: "New booking",
  booking_copy: "New booking",
  consolidation: "New booking",
  fob_nomination: "New booking",
  order_generic: "New booking",
  order_update: "Order update",
  pre_alert: "Pre-alert",
  arrival_notice: "Arrival",
  booking_rolled: "Roll-over",
  cargo_approval: "Space/cargo",
  shipment_import: "Import",
  direct_shipment: "Direct shipment",
  stuffing_update: "Stuffing",
  agent_report: "Agent report",
  rate_quote: "Rate quote",
  document: "Document",
  carrier_notice: "Carrier notice",
};

/** Short chip label for a classified Graph mail category slug. */
export function graphMailCategoryLabel(category: string): string {
  const mapped = GRAPH_MAIL_CHIP_LABEL[category];
  if (mapped) return mapped;
  return category
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Compact "2m ago" / "3h ago" / "5d ago" relative time from an ISO string. */
export function formatRelativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  return `${weeks}w ago`;
}
